/**
 * Maglia — maglia squadra dal template di Andrea (2026-10-07).
 *
 * Le forme arrivano da `src/data/maglia-template.ts`, generato da
 * `scripts/maglia_da_template.py` a partire da `scripts/maglia/template-maglia.svg`:
 * per cambiare modelli o grafica si modifica il template e si rilancia lo script.
 *
 * 15 modelli × 5 colori (corpo, dettaglio, accento, colletto, polsini), sponsor
 * fisso, toppe scudetto e Champions solo per il vincitore in carica, logo della
 * squadra in alto a destra (dalle misure medie in su: a 22 px non si leggerebbe).
 */

import { createElement, type ReactNode, useId } from "react";
import { MAGLIA } from "../../data/maglia-template";
import type { NodoMaglia, RuoloColore } from "./magliaTipi";
import { tr } from "../../i18n";

export type MagliaPattern =
  | "tinta_unita"
  | "meta_verticale"
  | "meta_verticale_inversa"
  | "crociata"
  | "fascia_verticale"
  | "tre_righe_verticali"
  | "righe_verticali"
  | "diagonale"
  | "diagonale_sx"
  | "croce_x"
  | "fascia_orizzontale"
  | "strisce_orizzontali"
  | "v"
  | "scacchi"
  | "maniche_contrasto";

export interface MagliaProps {
  pattern: MagliaPattern | string;
  coloreCorpo: string;
  coloreDettaglio: string;
  /** colore delle fasce secondarie (fascia verticale/orizzontale); default = dettaglio */
  coloreAccento?: string | null;
  coloreColletto?: string | null;
  colorePolsini?: string | null;
  /** vecchio colore "maniche": fa da colletto/polsini se mancano */
  coloreManiche?: string | null;
  patchScudetto?: boolean;
  patchChampions?: boolean;
  /** non piu' disegnata (Andrea 2026-10-07): resta per compatibilita' */
  patchSupercoppa?: boolean;
  /** logo della squadra, in alto a destra */
  logoDataUrl?: string | null;
  /** colore dello sponsor; vuoto = automatico (bianco sulle maglie scure) */
  coloreSponsor?: string | null;
  /** quale sponsor: "logo_nome" (logo + scritta, default) o "logo" */
  sponsor?: string | null;
  size?: number;
  className?: string;
}

/** Il modello usa il colore accento? (l'editor mostra il campo solo allora) */
export function modelloUsaAccento(pattern: string): boolean {
  const cerca = (n: NodoMaglia): boolean =>
    n.r === "accento" || (n.c ?? []).some(cerca) || (n.clip ?? []).some(cerca);
  return (MAGLIA.modelli[pattern] ?? []).some(cerca);
}

/** Luminosita' percepita di un colore #rrggbb (0 = nero, 1 = bianco). */
function luminosita(hex: string): number {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return 1;
  const [r, g, b] = [m[1], m[2], m[3]].map((x) => parseInt(x, 16) / 255);
  return 0.299 * r + 0.587 * g + 0.114 * b;
}

/** Colore originale dello sponsor nel template. */
export const SPONSOR_DEFAULT = "#2A0930";

/** Dalla misura in su si disegna il logo della squadra. */
const LATO_MIN_LOGO = 48;

export function Maglia({
  pattern,
  coloreCorpo,
  coloreDettaglio,
  coloreAccento,
  coloreColletto,
  colorePolsini,
  coloreManiche,
  patchScudetto = false,
  patchChampions = false,
  logoDataUrl,
  coloreSponsor,
  sponsor,
  size = 40,
  className = "",
}: MagliaProps) {
  const uid = useId().replace(/:/g, "");
  const colori: Record<RuoloColore, string> = {
    corpo: coloreCorpo,
    dettaglio: coloreDettaglio,
    accento: coloreAccento || coloreDettaglio,
    colletto: coloreColletto || coloreManiche || coloreDettaglio,
    polsini: colorePolsini || coloreManiche || coloreDettaglio,
    fisso: "#ffffff",
  };
  let nClip = 0;

  const disegna = (n: NodoMaglia, key: string | number): ReactNode => {
    if (n.t === "g") {
      return (
        <g key={key} transform={n.a?.transform}>
          {(n.c ?? []).map((c, i) => disegna(c, i))}
        </g>
      );
    }
    if (n.t === "image") {
      return <image key={key} href={n.src} {...n.a} />;
    }
    if (n.t === "clip") {
      const id = `maglia-${uid}-clip${nClip++}`;
      return (
        <g key={key}>
          <clipPath id={id}>
            {(n.clip ?? []).map((c, i) => createElement(c.t, { key: i, ...c.a }))}
          </clipPath>
          <g clipPath={`url(#${id})`}>{(n.c ?? []).map((c, i) => disegna(c, i))}</g>
        </g>
      );
    }
    const fill = n.r === "fisso" ? n.fill || "#ffffff" : colori[n.r ?? "corpo"];
    // bordo sottile dello stesso colore: copre la fessura tra le due meta' del
    // corpo (nel template si toccano senza sovrapporsi) e tra righe e corpo
    return createElement(n.t, { key, ...n.a, fill, stroke: fill, strokeWidth: 1.5 });
  };

  // solo la geometria (senza colori) delle forme di un modello, per i ritagli
  const sagoma = (nodi: NodoMaglia[]): ReactNode[] =>
    nodi.map((n, i) =>
      n.t === "g" ? (
        <g key={i} transform={n.a?.transform}>
          {sagoma(n.c ?? [])}
        </g>
      ) : (
        createElement(n.t, { key: i, ...n.a })
      ),
    );

  const modello = MAGLIA.modelli[pattern] ?? MAGLIA.modelli.tinta_unita;
  const conLogo = !!logoDataUrl && size >= LATO_MIN_LOGO;

  return (
    <svg
      viewBox={MAGLIA.viewBox}
      width={size}
      height={size}
      className={className}
      role="img"
      aria-label={tr("Maglia")}
    >
      {/* Il corpo e' ritagliato sulla sagoma della maglia: il bordo sottile che
          copre la fessura tra le due meta' non sborda nel collo (filo bianco
          sotto il colletto sulle maglie chiare, Andrea 2026-10-07). */}
      <clipPath id={`maglia-${uid}-sagoma`}>{sagoma(MAGLIA.modelli.tinta_unita)}</clipPath>
      <g clipPath={`url(#maglia-${uid}-sagoma)`}>{modello.map((n, i) => disegna(n, `m${i}`))}</g>
      {disegna(MAGLIA.colletto, "colletto")}
      {MAGLIA.polsini.map((n, i) => disegna(n, `p${i}`))}
      {/* Sponsor come sagoma (PNG trasparente a una tinta) riempita col colore
          scelto; automatico: bianco sulle maglie scure, viola del template
          sulle chiare (Andrea 2026-10-07). */}
      <mask id={`maglia-${uid}-sponsor`} style={{ maskType: "alpha" }}>
        {disegna(MAGLIA.sponsor[sponsor ?? ""] ?? MAGLIA.sponsor.logo_nome, "sponsor-mask")}
      </mask>
      <rect
        x={0}
        y={0}
        width="100%"
        height="100%"
        fill={coloreSponsor || (luminosita(coloreCorpo) < 0.35 ? "#FFFFFF" : SPONSOR_DEFAULT)}
        mask={`url(#maglia-${uid}-sponsor)`}
      />
      {patchScudetto && disegna(MAGLIA.scudetto, "scudetto")}
      {patchChampions && disegna(MAGLIA.champions, "champions")}
      {conLogo && (
        <image
          href={logoDataUrl ?? undefined}
          x={MAGLIA.logo.x}
          y={MAGLIA.logo.y}
          width={MAGLIA.logo.width}
          height={MAGLIA.logo.height}
          preserveAspectRatio="xMidYMid meet"
        />
      )}
    </svg>
  );
}
